import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Button, CircularProgress, Alert } from '@mui/material';
import RomanNumeralText from '../components/RomanNumeralText';
import { useChowaTheme } from '../config/chowaThemeContext';
import { getEngineDataByModelAndExhaust } from '../api_config';
import { fetchModelsBySeries, getSeriesColor } from '../model/UnitSeries';
import Card from '../config/ChowaCard';

// ダッシュボードコンポーネントをインポート
import TimeEngineHoursBarChart from './timeEngineHoursBarChart';
import AllEngineHoursBarChart from './50ALL_timeEngineHoursBarChart';

// 新しいカードコンポーネントをインポート
import ModelRegistrationCard from './ModelRegistrationCard';
import TodayOperatingCard from './TodayOperatingCard';

const SeriesDataPage = ({ seriesKey, selectedModel: initialSelectedModel, onModelSelect, onBackToSelection }) => {
  const { setSeriesColor, isDark } = useChowaTheme();
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(initialSelectedModel);
  const [engineData, setEngineData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState(null);

  const seriesColor = getSeriesColor(seriesKey);

  // シリーズ色を設定
  useEffect(() => {
    setSeriesColor(seriesKey);
  }, [setSeriesColor, seriesKey]);

  // 機種一覧を取得
  useEffect(() => {
    if (seriesKey) {
      fetchModelsForSeries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesKey]);

  // 初期選択モデルが設定されている場合、データを取得
  useEffect(() => {
    if (initialSelectedModel) {
      setSelectedModel(initialSelectedModel);
      fetchSeriesData(initialSelectedModel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedModel]);

  const fetchModelsForSeries = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const modelsData = await fetchModelsBySeries(seriesKey);
      setModels(modelsData);
      
      if (modelsData.length === 0) {
        setError(`${seriesKey}シリーズの機種データが見つかりませんでした。`);
      }
    } catch (err) {
      console.error('Error fetching models:', err);
      setError('機種データの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const fetchSeriesData = async (model) => {
    setDataLoading(true);
    try {
      // 新しい形式のデータ取得を使用
      const result = await getEngineDataByModelAndExhaust(seriesKey, model);
      
      setEngineData(result.engineData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setEngineData([]);
    } finally {
      setDataLoading(false);
    }
  };

  const handleModelSelect = (modelName) => {
    setSelectedModel(modelName);
    fetchSeriesData(modelName);
    if (onModelSelect) {
      onModelSelect(modelName);
    }
  };

  const handleBackToSelection = () => {
    setSelectedModel(null);
    setEngineData([]);
    if (onBackToSelection) {
      onBackToSelection();
    }
  };

  // データ表示中の場合
  if (selectedModel && engineData.length > 0) {
    const totalUnits = engineData.length;
    const operatingUnits = engineData.filter(item => {
      const today = new Date();
      const itemDate = new Date(item.timestamp);
      return itemDate.toDateString() === today.toDateString();
    }).length;

    return (
      <Box sx={{ p: 3 }}>
        {/* ヘッダー */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button 
            variant="outlined" 
            onClick={handleBackToSelection}
            sx={{ 
              mr: 2,
              color: seriesColor,
              borderColor: seriesColor,
              '&:hover': {
                borderColor: seriesColor,
                backgroundColor: `${seriesColor}20`
              }
            }}
          >
            ← 機種選択に戻る
          </Button>
          <Typography 
            variant="h4" 
            sx={{ 
              color: seriesColor,
              fontWeight: 'bold'
            }}
          >
            <RomanNumeralText text={selectedModel} /> データ
          </Typography>
        </Box>

        {/* 統計カード */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <ModelRegistrationCard totalUnits={totalUnits} seriesColor={seriesColor} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TodayOperatingCard operatingUnits={operatingUnits} seriesColor={seriesColor} />
          </Grid>
        </Grid>

        {/* チャート */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <TimeEngineHoursBarChart 
                data={engineData} 
                isDark={isDark}
                seriesColor={seriesColor}
                selectedModel={selectedModel}
              />
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <AllEngineHoursBarChart 
                data={engineData}
                isDark={isDark}
                seriesColor={seriesColor}
                selectedModel={selectedModel}
              />
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // 機種選択画面
  return (
    <Box sx={{ p: 4 }}>
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 4, 
          textAlign: 'center',
          color: seriesColor,
          fontWeight: 'bold'
        }}
      >
        {seriesKey}シリーズ - 機種選択
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress sx={{ color: seriesColor }} />
        </Box>
      )}

      {dataLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
          <CircularProgress sx={{ color: seriesColor, mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            {selectedModel} のデータを取得中...
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && models.length > 0 && (
        <>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3, 
              textAlign: 'center',
              color: 'text.secondary'
            }}
          >
            機種を選択してデータを取得してください
          </Typography>

          <Grid container spacing={2} justifyContent="center">
            {models.map((modelName) => (
              <Grid item key={modelName}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => handleModelSelect(modelName)}
                  disabled={dataLoading}
                  sx={{
                    backgroundColor: seriesColor,
                    color: 'white',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: seriesColor,
                      opacity: 0.8,
                    },
                    minWidth: '180px',
                    '&:disabled': {
                      backgroundColor: '#ccc',
                      color: '#666'
                    }
                  }}
                >
                  <RomanNumeralText text={modelName} />
                </Button>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default SeriesDataPage;
